/**
 * First-sign-in consent (MM-D-138, TMMAPP-0119 §A4; TMMAPI-0156 U1). A subscriber whose consent
 * records lack `TERMS` or `AUTO_RENEWAL` for the served `consumerTermsVersion` (an admin-created
 * subscription records none) gets this blocking dialog: the subscribe dialog's two boxes and their
 * approved wording (`SubscribeConsent`), with the seller under the title. There is no Cancel, and
 * Esc and the backdrop do nothing; Go to Settings is the one way out, as in the blocking subscribe
 * dialog (Settings and `/auth/*` stay reachable).
 *
 * Box 2 is the approved wording with the person's own renewal (TMMAPP-0124 item 6): the served
 * `/me/billing` `subscription.renewal` (its pre-tax amount, interval and date, TMMAPI-0160 U3), never
 * the public quote. When `renewal` is null (a complimentary, cancelled or ending subscription) there
 * is no renewal to consent to: box 2 is not shown and `AUTO_RENEWAL` is not asked; a `COMP`
 * subscription is never asked at all (`consentGate`).
 *
 * Accepting sends `POST /me/consents {kind, version}` once per kind still to record
 * (idempotent server-side), so a retry after a partial success sends only the rest. A
 * `422 CONSENT_REQUIRED` names the current version, unticks Terms and asks again for both kinds;
 * without a version it re-reads the bootstrap for the served one (item 12), and with none (or the
 * same one again) it shows the generic error rather than resend the same version forever.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { billingApi, type SubscriptionRenewal } from '@/api/billing';
import { CONSENT_KINDS, consentsApi, type ConsentKind } from '@/api/consents';
import { errorCopy } from '@/api/errorCopy';
import { resolveBootstrap } from '@/config/bootstrap';
import { consentRequired } from '@/api/errors';
import { SubmitButton } from '@/components/SubmitButton';
import { SellerLine } from '@/features/billing/SellerLine';
import { useConsumerTerms } from '@/features/billing/useConsumerTerms';
import { SETTINGS_PATH } from '@/features/settings/settingsHref';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { useMe } from '@/session/meHooks';
import {
  NO_CONSENT,
  renewalSentenceKey,
  type ConsentValues,
  type RenewalPartsTerms,
  type RenewalTerms,
} from './consumerTerms';
import { SubscribeConsent } from './SubscribeConsent';

interface FirstSignInConsentDialogProps {
  open: boolean;
  /** The served version the records were compared with. */
  version: string;
  /** The kinds with no record for `version`. */
  missing: ConsentKind[];
  /** Every missing kind is recorded. */
  onAccepted: () => void;
}

type RenewalState =
  { kind: 'loading' } | { kind: 'ready'; terms: RenewalTerms | null } | { kind: 'failed' };

/**
 * The served parts drive box 2. The promotion applies to the add-ons (TMMAPP-0129), never the
 * plan. A served promotion keeps that clause even when the sum is 0.
 */
function renewalParts(renewal: SubscriptionRenewal): RenewalPartsTerms {
  const promotionMinor = renewal.parts.promotionMinor;
  return {
    baseMinor: renewal.parts.baseMinor,
    addOnsMinor: renewal.parts.seatsMinor + renewal.parts.connectionsMinor + (promotionMinor ?? 0),
    includesPromotion: promotionMinor !== null,
    on: renewal.on,
  };
}

/**
 * Box 2's sentence from `/me/billing`'s `subscription.renewal` (TMMAPP-0124 item 6); null when there
 * is no renewal to consent to. A trial is `/me.trialEndsOn` (TMMAPP-0128), not a comparison with
 * the billing date, a line beside the parts sentence.
 */
async function loadRenewal(trialEndsOn: string | null): Promise<RenewalTerms | null> {
  const subscription = (await billingApi.profile()).subscription;
  const renewal = subscription?.renewal ?? null;
  if (renewal === null) return null;
  const parts = renewalParts(renewal);
  return {
    interval: renewal.interval,
    priceMinor: parts.baseMinor,
    currency: renewal.currency,
    trialEnd: null,
    promo: null,
    parts,
    trialNotice: trialEndsOn,
  };
}

/** The served terms version, re-read from the bootstrap; null when it serves none (or fails). */
async function readServedVersion(): Promise<string | null> {
  try {
    return (await resolveBootstrap()).consumerTerms.version;
  } catch {
    return null;
  }
}

export function FirstSignInConsentDialog({
  open,
  version,
  missing,
  onAccepted,
}: FirstSignInConsentDialogProps) {
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      // Blocking: Esc and the backdrop do nothing (MUI 9 has no disableEscapeKeyDown).
      onClose={() => undefined}
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: fullScreen ? undefined : { maxWidth: 560 },
          ...({ 'data-first-sign-in-consent': '' } as Record<string, string>),
        },
      }}
    >
      <ConsentBody titleId={titleId} version={version} missing={missing} onAccepted={onAccepted} />
    </Dialog>
  );
}

function ConsentBody({
  titleId,
  version: servedVersion,
  missing,
  onAccepted,
}: {
  titleId: string;
  version: string;
  missing: ConsentKind[];
  onAccepted: () => void;
}) {
  const { t } = useTranslation();
  const trialEndsOn = useMe().trialEndsOn;
  const terms = useConsumerTerms();
  const consentId = useId();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [consent, setConsent] = useState<ConsentValues>(NO_CONSENT);
  const [consentShown, setConsentShown] = useState(false);
  const [termsUpdated, setTermsUpdated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [renewal, setRenewal] = useState<RenewalState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // The box-2 sentence the person ticked (a different one needs a fresh tick, as in subscribe).
  const [renewalTickedFor, setRenewalTickedFor] = useState<string | null>(null);
  // What is still to record: the missing kinds, or both after a 422 named a newer version.
  const pending = useRef<{ version: string; kinds: ConsentKind[] }>({
    version: servedVersion,
    kinds: missing,
  });
  const submitting = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void loadRenewal(trialEndsOn)
      .then((terms) => {
        if (!cancelled) setRenewal({ kind: 'ready', terms });
      })
      .catch(() => {
        if (!cancelled) setRenewal({ kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, trialEndsOn]);

  const retryRenewal = useCallback(() => {
    setRenewal({ kind: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const focusFirstInvalid = useCallback(() => {
    window.requestAnimationFrame(() => {
      contentRef.current
        ?.querySelector<HTMLElement>('[data-subscribe-consent] input[aria-invalid="true"]')
        ?.focus();
    });
  }, []);

  const renewalTerms = renewal.kind === 'ready' ? renewal.terms : null;
  // No served renewal: nothing renews, so auto-renewal is not asked (item 6).
  const askRenewal = !(renewal.kind === 'ready' && renewal.terms === null);
  const renewalKey = renewalSentenceKey(renewalTerms);
  const autoRenewalTicked = consent.autoRenewal && renewalTickedFor === renewalKey;

  async function handleAccept() {
    if (submitting.current) return;
    if (!consent.terms || (askRenewal && !autoRenewalTicked)) {
      setConsentShown(true);
      focusFirstInvalid();
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    if (!askRenewal) {
      pending.current = {
        ...pending.current,
        kinds: pending.current.kinds.filter((kind) => kind !== 'AUTO_RENEWAL'),
      };
    }
    try {
      while (pending.current.kinds.length > 0) {
        const [kind, ...rest] = pending.current.kinds;
        await consentsApi.record(kind!, pending.current.version);
        pending.current = { ...pending.current, kinds: rest };
      }
      onAccepted();
    } catch (failure) {
      const ask = consentRequired(failure);
      // No version named: the bootstrap is read again for the served one (item 12).
      const named = ask?.currentTermsVersion ?? null;
      const reread = ask !== null && named === null ? await readServedVersion() : null;
      const version = named ?? (reread !== pending.current.version ? reread : null);
      if (ask !== null && version !== null) {
        // The terms changed: both kinds are asked again for the version named.
        pending.current = {
          version,
          kinds: CONSENT_KINDS.filter((kind) => askRenewal || kind !== 'AUTO_RENEWAL'),
        };
        setConsent((current) => ({ ...current, terms: false }));
        setTermsUpdated(true);
        focusFirstInvalid();
      } else if (ask !== null) {
        // Still no newer version: never resend the same one; the generic error says so.
        setError(t('errors.generic'));
      } else {
        setError(errorCopy(t, failure, 'errors.generic'));
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  const required = t('subscribeDialog.consent.required');
  const termsError = consent.terms
    ? null
    : termsUpdated
      ? t('subscribeDialog.consent.termsUpdated')
      : consentShown
        ? required
        : null;
  const autoRenewalError =
    autoRenewalTicked || !consentShown || renewalTerms === null ? null : required;

  return (
    <>
      <DialogTitle id={titleId}>{t('subscribeDialog.firstSignIn.title')}</DialogTitle>
      <SellerLine dialog />
      <DialogContent ref={contentRef} sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body1">{t('subscribeDialog.firstSignIn.body')}</Typography>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <SubscribeConsent
            values={{ ...consent, autoRenewal: autoRenewalTicked }}
            onChange={(next) => {
              if (next.autoRenewal && !autoRenewalTicked) setRenewalTickedFor(renewalKey);
              setConsent(next);
            }}
            renewal={renewalTerms}
            autoRenewalShown={askRenewal}
            priceFailed={renewal.kind === 'failed'}
            onRetryPrice={retryRenewal}
            priceLoadingShown={false}
            termsUrl={terms.termsUrl}
            privacyUrl={terms.privacyUrl}
            termsError={termsError}
            autoRenewalError={autoRenewalError}
            disabled={busy}
            idPrefix={consentId}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button component={RouterLink} to={SETTINGS_PATH} disabled={busy}>
          {t('subscribeDialog.goToSettings')}
        </Button>
        <SubmitButton onClick={() => void handleAccept()} loading={busy}>
          {t('subscribeDialog.firstSignIn.accept')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
